import { WorkPreparation } from "@/types/arbetsberedning";

export interface CompletenessRule {
  key: string;
  label: string;
  ok: boolean;
  blocking: boolean;
  hint?: string;
}

export interface CompletenessResult {
  rules: CompletenessRule[];
  percent: number;
  missingBlocking: CompletenessRule[];
  canSubmit: boolean;
}

export function evaluateCompleteness(wp: WorkPreparation | null): CompletenessResult {
  const f = wp?.fields;
  const moment = f?.moment ?? [];
  const kontroller = f?.kontrollpunkter ?? [];
  const allRisker = moment.flatMap((m) => m.risker ?? []);
  const kompetenser = f?.kompetensVerifieringar ?? [];
  const material = f?.befintligtMaterialKontroll;
  const materialResolved =
    material?.status === "kontrollerat_utan_fynd" ||
    (material?.status === "inte_relevant" && !!material.motiveringInteRelevant?.trim()) ||
    ((material?.status === "misstanke" || material?.status === "patraffat") && !!material.atgarder?.trim());

  // Asbestkedjan: vid misstanke eller fynd krävs verifierad, giltig behörighet.
  const asbestosTriggered = material?.status === "misstanke" || material?.status === "patraffat";

  const notExpired = (d?: string) => !d || new Date(d) >= new Date(new Date().toDateString());
  const hasVerifiedCompetence = (keywords: string[]) =>
    kompetenser.some((k) => {
      const text = `${k.krav ?? ""} ${k.verifiering ?? ""}`.toLowerCase();
      const relevant = keywords.some((w) => text.includes(w));
      return relevant && k.status === "verifierad" && notExpired(k.giltigTill) && !!k.verifieradAv?.trim();
    });

  const asbestosCleared = hasVerifiedCompetence(["asbest"]);

  // Specialkedjor för heta arbeten och elarbete: triggas av text i beskrivning,
  // moment eller risker och kräver verifierad behörighet innan momentet får utföras.
  const haystack = [
    f?.beskrivArbetet ?? "",
    ...moment.map((m) => `${m.titel ?? ""} ${m.syfte ?? ""} ${m.utrustning ?? ""}`),
    ...allRisker.map((r) => `${r.risk ?? ""} ${r.tekniskAtgard ?? ""}`),
  ]
    .join(" ")
    .toLowerCase();

  const hotWorkTriggered = ["heta arbeten", "svets", "skärbränn", "lödning", "vinkelslip", "gasol"].some((w) =>
    haystack.includes(w),
  );
  const hotWorkCleared = hasVerifiedCompetence(["heta arbeten", "svets", "brandfarlig"]);

  const electricalTriggered = ["elarbete", "elinstallation", "spänning", "högspänning", "ställverk", "elrisk"].some(
    (w) => haystack.includes(w),
  );
  const electricalCleared = hasVerifiedCompetence(["el", "esa", "elsäkerhet", "spänning"]);

  // Kemiska produkter: krävs säkerhetsdatablad, eller en motivering om inga kemikalier används.
  const sheets = f?.sakerhetsdatablad ?? [];
  const chemicalTriggered =
    sheets.length > 0 ||
    !!f?.kemikalier?.trim() ||
    !!f?.hanteringMaterial?.farligtAvfall ||
    ["kemikal", "lim ", "epoxi", "lösningsmedel", "fog", "härdplast", "isocyanat", "färg", "drivmedel"].some((w) =>
      haystack.includes(w),
    );
  const chemicalCleared =
    (sheets.length > 0 && sheets.every((s) => s.harFil)) ||
    (sheets.length === 0 && !!f?.sakerhetsdatabladEjAktuellt?.trim());

  const rules: CompletenessRule[] = [
    {
      key: "beskrivning",
      label: "Arbetet är beskrivet",
      ok: !!(f?.beskrivArbetet && f.beskrivArbetet.trim().length > 20),
      blocking: true,
      hint: "Beskriv vad som ska göras, minst ett par meningar.",
    },
    {
      key: "plats",
      label: "Plats och tidpunkt är angivna",
      ok: !!(f?.arbetsplats || f?.entreprenorInput?.var) && !!(f?.startdatum || f?.entreprenorInput?.nar),
      blocking: true,
    },
    {
      key: "moment",
      label: "Momentordning finns (minst 2 moment)",
      ok: moment.length >= 2,
      blocking: true,
      hint: "Arbetet ska delas upp i moment i den ordning de utförs.",
    },
    {
      key: "risker",
      label: "Risker är identifierade per moment",
      ok: moment.length > 0 && moment.every((m) => (m.risker ?? []).length > 0),
      blocking: true,
      hint: "Varje moment behöver minst en identifierad risk.",
    },
    {
      key: "atgarder",
      label: "Varje risk har en konkret åtgärd",
      ok:
        allRisker.length > 0 &&
        allRisker.every((r) => !!(r.organisatoriskAtgard?.trim() || r.tekniskAtgard?.trim())),
      blocking: true,
    },
    {
      key: "ansvar",
      label: "Ansvarig är utpekad för moment eller åtgärd",
      ok: moment.length > 0 && moment.every((m) => !!m.ansvarigRoll?.trim() || (m.risker ?? []).some((r) => !!r.ansvarig?.trim())),
      blocking: true,
    },
    {
      key: "kontroller",
      label: "Kontroll- och uppföljningspunkter finns",
      ok: kontroller.length > 0,
      blocking: true,
    },
    {
      key: "forestart",
      label: "Minst en kontroll före start",
      ok: kontroller.some((k) => k.nar === "före start"),
      blocking: true,
    },
    {
      key: "kompetens",
      label: "Kompetenser och behörigheter är verifierade",
      ok: kompetenser.length > 0 && kompetenser.every((k) => {
        if (k.status === "ej_aktuell") return !!k.motiveringEjAktuell?.trim();
        const filled = !!k.personEllerRoll.trim() && !!k.krav.trim() && !!k.verifiering.trim() &&
          !!k.verifieradAv.trim() && !!k.verifieradDatum;
        const notExpired = !k.giltigTill || new Date(k.giltigTill) >= new Date(new Date().toDateString());
        return k.status === "verifierad" && filled && notExpired;
      }),
      blocking: true,
      hint: "Varje krav måste markeras som verifierat med namn, datum och giltigt intyg – eller motiveras som ej aktuellt.",
    },

    {
      key: "befintligt-material",
      label: "Asbest och andra befintliga material är kontrollerade",
      ok: !!materialResolved,
      blocking: true,
      hint: "Ta aktiv ställning och dokumentera åtgärd vid misstanke eller fynd.",
    },
    {
      key: "asbest-behorighet",
      label: "Vid asbestfynd: tillstånd och utbildning är verifierade",
      ok: !asbestosTriggered || asbestosCleared,
      blocking: true,
      hint:
        "Vid misstanke eller fynd av asbest krävs en verifierad, giltig behörighet för asbest (tillstånd från Arbetsmiljöverket och utbildningsbevis) innan arbetet får starta.",
    },
    {
      key: "heta-arbeten-behorighet",
      label: "Vid heta arbeten: certifikat är verifierat",
      ok: !hotWorkTriggered || hotWorkCleared,
      blocking: true,
      hint:
        "Arbetet innehåller heta arbeten (svetsning, skärning, slipning eller liknande). Certifikat för heta arbeten och tillstånd med brandvakt ska vara verifierade innan start.",
    },
    {
      key: "el-behorighet",
      label: "Vid elarbete: elbehörighet är verifierad",
      ok: !electricalTriggered || electricalCleared,
      blocking: true,
      hint:
        "Arbetet berör el eller spänningsförande delar. Elbehörighet och skötselinstruktion enligt ELSÄK-FS 2022:1 ska vara verifierade innan start.",
    },
    {
      key: "sakerhetsdatablad",
      label: "Säkerhetsdatablad finns för kemiska produkter",
      ok: !chemicalTriggered || chemicalCleared,
      blocking: true,
      hint:
        "Arbetet innehåller kemiska produkter. Registrera varje produkt och ladda upp säkerhetsdatabladet – eller skriv en motivering till varför inga kemikalier används.",
    },
    {
      key: "utforare",
      label: "Vilka som utför arbetet är angivet",
      ok: (f?.vilkaUtför ?? []).length > 0 || !!f?.entreprenorInput?.antalPersoner,
      blocking: false,
    },
    {
      key: "utrustning",
      label: "Utrustning/maskiner är angivet",
      ok: (f?.verktygMaskiner ?? []).length > 0 || !!f?.entreprenorInput?.utrustning?.trim(),
      blocking: false,
    },
    {
      key: "uppfoljning",
      label: "Uppföljning är beskriven",
      ok: !!f?.uppföljning?.trim(),
      blocking: false,
    },
    {
      key: "upprattare",
      label: "Upprättad av och datum",
      ok: !!f?.gjordAv?.trim() && !!f?.datum,
      blocking: false,
    },
  ];

  const okCount = rules.filter((r) => r.ok).length;
  const percent = rules.length ? Math.round((okCount / rules.length) * 100) : 0;
  const missingBlocking = rules.filter((r) => r.blocking && !r.ok);

  return { rules, percent, missingBlocking, canSubmit: missingBlocking.length === 0 };
}
